// Operator shipping rule (2026-08-31, reaffirmed with plant packages in
// I-03 S3-01.5): the facility's FedEx priority-overnight logistics are
// built around live-animal shipments, so plant-only boxes are not
// offered. The backend refuses a plant-only cart at order creation with
// 422 {error: 'PLANTS_SHIP_WITH_ANIMALS'}.
//
// ONE sentence, used by the cart advisory, the checkout refusal and the
// /plants page copy — a customer who reads it at the cart must not meet
// a differently-worded version two steps later. It must stay byte-equal
// to the backend's 422 `message` (store/views.py), which the checkout
// toast prefers. "Plants", not "cuttings": pups exist now.
export const PLANTS_SHIP_WITH_ANIMALS_MESSAGE =
  'Plants ship alongside animal orders — add a frog or gecko to your order and the plants ride along.';

// The machine code the API returns alongside the message.
export const PLANTS_SHIP_WITH_ANIMALS_CODE = 'PLANTS_SHIP_WITH_ANIMALS';

// Batch 34 C5 (operator, 2026-10-07: "we don't want to hit them over the
// head with it… but it's worth telling them"): the ONE reading of "this
// cart holds no animal yet", shared by the cart advisory, the checkout
// guard and the add-to-cart confirmation. Lifted out of CartPage (which
// re-exports it) so the confirmation in CartContext reads the same rule.
// A line is a plant when its variation's species_type is PLANT (1).
export const PLANT_SPECIES_TYPE = 1;

export const isPlantVariation = (variation) => (
  variation?.species_type === PLANT_SPECIES_TYPE
);

export const isPlantOnlyCart = (items) => {
  const lines = Array.isArray(items) ? items : [];
  if (lines.length === 0) return false;
  return lines.every((item) => isPlantVariation(item?.product_variation));
};

// The add-to-cart confirmation. A PLANT added to a cart that holds no
// animal yet carries the one sentence as an INFO notice (never a warning)
// that stays a little longer so it can be read, then goes; a plant added
// beside an animal, and anything else, says only "Added".
//
// Batch 34 review NIT (operator: "don't hit them over the head"): the
// sentence is said at most ONCE PER BROWSER SESSION. Later plant adds to a
// still-plant-only cart say just "Added" — the /plants page line and the
// cart advisory still carry it. If sessionStorage is unavailable (private
// mode, blocked site data) the sentence is shown rather than lost.
export const RIDE_ALONG_TOAST_MS = 6000;
export const RIDE_ALONG_SEEN_KEY = 'ff_plants_ride_along_said';

const rideAlongAlreadySaid = () => {
  try {
    return window.sessionStorage.getItem(RIDE_ALONG_SEEN_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const markRideAlongSaid = () => {
  try {
    window.sessionStorage.setItem(RIDE_ALONG_SEEN_KEY, '1');
  } catch (e) {
    // Storage refused: nothing remembers it, so the next add says it again.
  }
};

export const addToCartConfirmation = (quantity, productName, variation, itemsAfter) => {
  const added = `Added ${quantity} ${productName} to cart`;
  if (isPlantVariation(variation) && isPlantOnlyCart(itemsAfter) && !rideAlongAlreadySaid()) {
    markRideAlongSaid();
    return {
      message: `${added}. ${PLANTS_SHIP_WITH_ANIMALS_MESSAGE}`,
      type: 'info',
      duration: RIDE_ALONG_TOAST_MS,
    };
  }
  return { message: added, type: 'success', duration: undefined };
};
